/**
 * Frontend Constants
 * Shared values used across the application
 */

import { DISPLAY_NAME_INPUT_MAX_LENGTH } from '@parlay-picker/shared/contracts/authContracts';
// Import shared sportsbook types (consumers should import these directly from @parlay-picker/shared/sportsbook)
import { getOddsApiRegions, type LeagueKey } from '@parlay-picker/shared/league';
import type {
  EuGameMarketSportsbookId,
  OddsApiSportsbookId as OddsApiType,
  Sportsbook as SportsbookType,
} from '@parlay-picker/shared/sportsbook';
import {
  EU_GAME_MARKET_SPORTSBOOKS,
  EU_ODDS_API_SPORTSBOOKS,
  SPORTSBOOKS_ODDS_API,
} from '@parlay-picker/shared/sportsbook';

// =============================================================================
// Validation Limits (must match server/config/constants.ts)
// =============================================================================

export const VALIDATION_LIMITS = {
  EMAIL_MAX_LENGTH: 255,
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  FIRST_NAME_MAX_LENGTH: 30,
  LAST_NAME_MAX_LENGTH: 30,
  DISPLAY_NAME_MAX_LENGTH: DISPLAY_NAME_INPUT_MAX_LENGTH,
} as const;

// =============================================================================
// Badge Types & Constants (re-exported from shared)
// =============================================================================

export type {
  BadgeStyle,
  ConfidenceStyle,
  TierConfig,
  VoidReasonConfig,
} from '@parlay-picker/shared/badgeConfig';

export {
  CONFIDENCE_LEVELS,
  FLAG_BADGES,
  ORIGIN_BADGES,
  PARLAY_TIERS,
  RESULT_BADGES,
  SCORE_REC_BADGES,
  VOID_REASON_CONFIG,
} from '@parlay-picker/shared/badgeConfig';

// =============================================================================
// Prop Type Display Names
// =============================================================================

export const PROP_DISPLAY_NAMES: Record<string, string> = {
  points: 'Points',
  rebounds: 'Rebounds',
  assists: 'Assists',
  threes: '3-Pointers',
  blocks: 'Blocks',
  steals: 'Steals',
  turnovers: 'Turnovers',
  field_goals: 'Field Goals',
  frees_made: 'Free Throws',
  // Combo props - all known forms
  'pts+reb+ast': 'PRA',
  pts_reb_ast: 'PRA',
  pra: 'PRA',
  'pts+reb': 'Pts+Reb',
  pts_reb: 'Pts+Reb',
  pr: 'Pts+Reb',
  'pts+ast': 'Pts+Ast',
  pts_ast: 'Pts+Ast',
  pa: 'Pts+Ast',
  'reb+ast': 'Reb+Ast',
  reb_ast: 'Reb+Ast',
  ra: 'Reb+Ast',
  blocks_steals: 'Blk+Stl',
  blk_stl: 'Blk+Stl',
  bs: 'Blk+Stl',
};

// =============================================================================
// Sportsbook Configuration
// =============================================================================

export interface SportsbookConfig {
  id: SportsbookType;
  name: string;
  abbr: string; // Short abbreviation for compact header display
  dot: string; // Tailwind bg class for colored dot
  bg: string; // Tailwind bg class for badge background
  text: string; // Tailwind text class for badge text
  maxLegs: number; // Maximum legs allowed in a parlay at this sportsbook
  oddsApiSupported: boolean; // Can be used for AI pick generation
  sharpSportsSupported: boolean; // Can be linked via SharpSports
  requiresExtension: boolean; // Requires browser extension for linking
}

/**
 * Unified sportsbook config - used for both Odds API (pick generation) and SharpSports (account linking)
 * Each sportsbook has a unique color for visual differentiation
 */
export const SPORTSBOOKS: Record<string, SportsbookConfig> = {
  // Odds API supported (for pick generation)
  fanduel: {
    id: 'fanduel',
    name: 'FanDuel',
    abbr: 'FD',
    dot: 'bg-theme-primary',
    bg: 'bg-theme-primary/15',
    text: 'text-theme-primary',
    maxLegs: 25,
    oddsApiSupported: true,
    sharpSportsSupported: true,
    requiresExtension: true,
  },
  draftkings: {
    id: 'draftkings',
    name: 'DraftKings',
    abbr: 'DK',
    dot: 'bg-theme-success',
    bg: 'bg-theme-success/15',
    text: 'text-theme-success',
    maxLegs: 12,
    oddsApiSupported: true,
    sharpSportsSupported: true,
    requiresExtension: true,
  },
  betmgm: {
    id: 'betmgm',
    name: 'BetMGM',
    abbr: 'MGM',
    dot: 'bg-theme-warning',
    bg: 'bg-theme-warning/15',
    text: 'text-theme-warning',
    maxLegs: 15,
    oddsApiSupported: true,
    sharpSportsSupported: true,
    requiresExtension: true,
  },
  caesars: {
    id: 'caesars',
    name: 'Caesars',
    abbr: 'CZR',
    dot: 'bg-theme-success',
    bg: 'bg-theme-success/15',
    text: 'text-theme-success',
    maxLegs: 25,
    oddsApiSupported: true,
    sharpSportsSupported: true,
    requiresExtension: false,
  },
  espnbet: {
    id: 'espnbet',
    name: 'ESPN BET',
    abbr: 'ESPN',
    dot: 'bg-theme-danger',
    bg: 'bg-theme-danger/15',
    text: 'text-theme-danger',
    maxLegs: 20,
    oddsApiSupported: true,
    sharpSportsSupported: false,
    requiresExtension: false,
  },
  betrivers: {
    id: 'betrivers',
    name: 'BetRivers',
    abbr: 'BR',
    dot: 'bg-sky-500',
    bg: 'bg-sky-500/15',
    text: 'text-sky-400',
    maxLegs: 12,
    oddsApiSupported: true,
    sharpSportsSupported: true,
    requiresExtension: true,
  },
  fanatics: {
    id: 'fanatics',
    name: 'Fanatics',
    abbr: 'FAN',
    dot: 'bg-indigo-500',
    bg: 'bg-indigo-500/15',
    text: 'text-indigo-400',
    maxLegs: 25,
    oddsApiSupported: true,
    sharpSportsSupported: true,
    requiresExtension: true,
  },
  // SharpSports only (for account linking, not pick generation)
  prizepicks: {
    id: 'prizepicks',
    name: 'PrizePicks',
    abbr: 'PP',
    dot: 'bg-purple-500',
    bg: 'bg-purple-500/15',
    text: 'text-purple-400',
    maxLegs: 6,
    oddsApiSupported: false,
    sharpSportsSupported: true,
    requiresExtension: true,
  },
  underdog: {
    id: 'underdog',
    name: 'Underdog',
    abbr: 'UD',
    dot: 'bg-theme-warning',
    bg: 'bg-theme-warning/15',
    text: 'text-theme-warning',
    maxLegs: 8,
    oddsApiSupported: false,
    sharpSportsSupported: true,
    requiresExtension: true,
  },
  sleeper: {
    id: 'sleeper',
    name: 'Sleeper',
    abbr: 'SLP',
    dot: 'bg-cyan-500',
    bg: 'bg-cyan-500/15',
    text: 'text-cyan-400',
    maxLegs: 8,
    oddsApiSupported: false,
    sharpSportsSupported: true,
    requiresExtension: true,
  },
  hardrock: {
    id: 'hardrock',
    name: 'Hard Rock',
    abbr: 'HR',
    dot: 'bg-orange-500',
    bg: 'bg-orange-500/15',
    text: 'text-orange-400',
    maxLegs: 20,
    oddsApiSupported: false,
    sharpSportsSupported: true,
    requiresExtension: false,
  },
  betfred: {
    id: 'betfred',
    name: 'BetFred',
    abbr: 'BF',
    dot: 'bg-rose-500',
    bg: 'bg-rose-500/15',
    text: 'text-rose-400',
    maxLegs: 15,
    oddsApiSupported: false,
    sharpSportsSupported: true,
    requiresExtension: false,
  },
  fliff: {
    id: 'fliff',
    name: 'Fliff',
    abbr: 'FL',
    dot: 'bg-pink-500',
    bg: 'bg-pink-500/15',
    text: 'text-pink-400',
    maxLegs: 6,
    oddsApiSupported: false,
    sharpSportsSupported: true,
    requiresExtension: false,
  },
};

// Helper functions for filtering sportsbooks

// Config type whose id is an Odds API book (type-safe for pick generation)
export type OddsApiSportsbookConfig = Omit<SportsbookConfig, 'id'> & {
  id: OddsApiType;
};

// Type guard: is this id an Odds API book?
export function isOddsApiSportsbook(id: SportsbookType): id is OddsApiType {
  return (SPORTSBOOKS_ODDS_API as readonly string[]).includes(id);
}

// Returns configs whose id is an Odds API book (properly typed, no casting)
export const getOddsApiSportsbookConfigs = (): OddsApiSportsbookConfig[] =>
  Object.values(SPORTSBOOKS).filter((s): s is OddsApiSportsbookConfig => isOddsApiSportsbook(s.id));

// Legacy helpers (less type-safe, kept for backwards compatibility)
export const getOddsApiSportsbooks = (): SportsbookConfig[] =>
  Object.values(SPORTSBOOKS).filter((s) => s.oddsApiSupported);

export const getSharpSportsSportsbooks = (): SportsbookConfig[] =>
  Object.values(SPORTSBOOKS).filter((s) => s.sharpSportsSupported);

export const getSportsbooksList = (): SportsbookConfig[] => Object.values(SPORTSBOOKS);

// =============================================================================
// EU Sportsbook Configs (for EuroLeague)
// =============================================================================

/** Display config for a EuroLeague book; its id is the Odds API key. */
export type EuSportsbookConfig = Omit<SportsbookConfig, 'id'> & { id: EuGameMarketSportsbookId };

export const EU_SPORTSBOOKS: Record<EuGameMarketSportsbookId, EuSportsbookConfig> = {
  pinnacle: {
    id: 'pinnacle',
    name: 'Pinnacle',
    abbr: 'PIN',
    dot: 'bg-yellow-500',
    bg: 'bg-yellow-500/15',
    text: 'text-yellow-400',
    maxLegs: 15,
    oddsApiSupported: true,
    sharpSportsSupported: false,
    requiresExtension: false,
  },
  sport888: {
    id: 'sport888',
    name: '888sport',
    abbr: '888',
    dot: 'bg-fuchsia-500',
    bg: 'bg-fuchsia-500/15',
    text: 'text-fuchsia-400',
    maxLegs: 15,
    oddsApiSupported: true,
    sharpSportsSupported: false,
    requiresExtension: false,
  },
  williamhill: {
    id: 'williamhill',
    name: 'William Hill',
    abbr: 'WH',
    dot: 'bg-violet-500',
    bg: 'bg-violet-500/15',
    text: 'text-violet-400',
    maxLegs: 15,
    oddsApiSupported: true,
    sharpSportsSupported: false,
    requiresExtension: false,
  },
};

/** The books a league's pickers list: every US book for US leagues, the EuroLeague books otherwise. */
export function getSportsbookOptionsForLeague(
  leagueKey: LeagueKey,
): Array<SportsbookConfig | EuSportsbookConfig> {
  return getOddsApiRegions(leagueKey) === 'us'
    ? getSportsbooksList()
    : EU_GAME_MARKET_SPORTSBOOKS.map((book) => EU_SPORTSBOOKS[book]);
}

/** The books a league's pick generation and prop browsing can use. */
export function getChoosableBookConfigs(
  leagueKey: LeagueKey,
): Array<OddsApiSportsbookConfig | EuSportsbookConfig> {
  return getOddsApiRegions(leagueKey) === 'us'
    ? getOddsApiSportsbookConfigs()
    : EU_GAME_MARKET_SPORTSBOOKS.map((book) => EU_SPORTSBOOKS[book]);
}

/**
 * Limit a book list to those with live odds coverage, always keeping the currently selected
 * book so it still renders. A null/empty covered set means "coverage unknown" (loading or
 * error), so the full list is returned unchanged (fail-safe, picker never goes empty).
 */
export function limitSportsbooksToCovered<B extends { id: string }>(
  books: B[],
  covered: string[] | null,
  selectedId: string,
): B[] {
  if (!covered || covered.length === 0) return books;
  const allow = new Set(covered);
  return books.filter((book) => allow.has(book.id) || book.id === selectedId);
}

/** Check if a book key is an EU sportsbook */
export function isEuSportsbook(bookKey: string): boolean {
  return (EU_ODDS_API_SPORTSBOOKS as readonly string[]).includes(bookKey);
}
